import { apiUrl } from "../api/endpoints";
import React, { useState, useContext, useMemo, useEffect } from 'react';
import '../Components/modal.css';
import axios from 'axios';

import { useAuth } from '../hooks/AuthProvider';
import Select from 'react-select';
import Alert from '../Components/Alert';
import { SearchBox } from '@mapbox/search-js-react';

import { LanguageContext } from '../context/LanguageContext';
import locTranslations from '../languages/location.json';
import { NameIcon, AddNewIcon } from '../svg/UtilsIcons';
import { DeviceIdIcon, OrganizationIcon } from '../svg/DeviceIcons';
import { getErrorMessage } from '../utils/errorUtils';
import { buildDeviceDropdownOption } from '../utils/dropdownLabels';
import { getDeviceLocationId } from '../utils/listRowIds';

/** Same address icon as AddLocation */
const AddressIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
    >
        <path
            d="M8.39545 15.5054L8.38811 15.5067L8.34078 15.53L8.32745 15.5327L8.31811 15.53L8.27078 15.5067C8.26367 15.5045 8.25834 15.5056 8.25478 15.51L8.25211 15.5167L8.24078 15.802L8.24411 15.8154L8.25078 15.824L8.32011 15.8734L8.33011 15.876L8.33811 15.8734L8.40745 15.824L8.41545 15.8134L8.41811 15.802L8.40678 15.5174C8.405 15.5103 8.40123 15.5063 8.39545 15.5054ZM8.57211 15.43L8.56345 15.4314L8.44011 15.4934L8.43345 15.5L8.43145 15.5074L8.44345 15.794L8.44678 15.802L8.45211 15.8067L8.58611 15.8687C8.59456 15.8709 8.601 15.8692 8.60545 15.8634L8.60811 15.854L8.58545 15.4447C8.58323 15.4367 8.57878 15.4318 8.57211 15.43ZM8.09545 15.4314C8.09251 15.4296 8.089 15.429 8.08565 15.4298C8.08229 15.4305 8.07936 15.4325 8.07745 15.4354L8.07345 15.4447L8.05078 15.854C8.05123 15.862 8.055 15.8674 8.06211 15.87L8.07211 15.8687L8.20611 15.8067L8.21278 15.8014L8.21545 15.794L8.22678 15.5074L8.22478 15.4994L8.21811 15.4927L8.09545 15.4314Z"
            fill="#00C0C7"
        />
        <path
            d="M8 1.33337C9.5913 1.33337 11.1174 1.96551 12.2426 3.09073C13.3679 4.21595 14 5.74207 14 7.33337C14 9.38271 12.8827 11.06 11.7053 12.2634C11.1171 12.8581 10.4753 13.3973 9.788 13.874L9.504 14.0674L9.37067 14.156L9.11933 14.316L8.89533 14.4527L8.618 14.614C8.42976 14.7215 8.21675 14.778 8 14.778C7.78325 14.778 7.57024 14.7215 7.382 14.614L7.10467 14.4527L6.758 14.2394L6.63 14.156L6.35667 13.974C5.61522 13.4724 4.9246 12.8994 4.29467 12.2634C3.11733 11.0594 2 9.38271 2 7.33337C2 5.74207 2.63214 4.21595 3.75736 3.09073C4.88258 1.96551 6.4087 1.33337 8 1.33337ZM8 5.33337C7.73736 5.33337 7.47728 5.38511 7.23463 5.48561C6.99198 5.58612 6.7715 5.73344 6.58579 5.91916C6.40007 6.10488 6.25275 6.32536 6.15224 6.56801C6.05173 6.81066 6 7.07073 6 7.33337C6 7.59602 6.05173 7.85609 6.15224 8.09874C6.25275 8.34139 6.40007 8.56187 6.58579 8.74759C6.7715 8.9333 6.99198 9.08062 7.23463 9.18113C7.47728 9.28164 7.73736 9.33337 8 9.33337C8.53043 9.33337 9.03914 9.12266 9.41421 8.74759C9.78929 8.37251 10 7.86381 10 7.33337C10 6.80294 9.78929 6.29423 9.41421 5.91916C9.03914 5.54409 8.53043 5.33337 8 5.33337Z"
            fill="#00C0C7"
        />
    </svg>
);

export const EditLocation = ({ location, refresh, setState, devices = [], devicesLoading = false }) => {
    const { permissions } = useAuth();
    const canEditLocations = permissions['edit_locations'];

    const { lang } = useContext(LanguageContext);
    const t = locTranslations[lang];
    const isRtl = ['ar', 'ur'].includes(lang);

    const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;

    const [formData, setFormData] = useState({
        name: location?.name || '',
        address: location?.address || '',
        latitude: location?.latitude ?? '',
        longitude: location?.longitude ?? '',
        deviceIds: Array.isArray(location?.device_ids)
            ? location.device_ids
            : Array.isArray(location?.devices)
                ? location.devices
                    .map((device) => Number(device?.id ?? device?._row_id))
                    .filter((id) => Number.isFinite(id) && id > 0)
                : location?.device_id
                    ? [location.device_id]
                    : [],
        organizationIds: []
    });

    const [isGeocoding, setIsGeocoding] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [lastGeocodedAddress, setLastGeocodedAddress] = useState('');
    const [organizations, setOrganizations] = useState([]);
    const [organizationsLoading, setOrganizationsLoading] = useState(false);


    useEffect(() => {
        let mounted = true;

        const loadOrganizations = async () => {
            setOrganizationsLoading(true);
            try {
                const res = await axios.get(apiUrl('location/organization-options'));
                if (!mounted) return;

                const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
                setOrganizations(rows);
                setFormData((prev) => ({
                    ...prev,
                    organizationIds: rows
                        .filter((organization) => Number(organization?.location_id) === Number(location?.id))
                        .map((organization) => Number(organization.id))
                        .filter((id) => Number.isInteger(id) && id > 0)
                }));
            } catch (error) {
                if (mounted) {
                    setOrganizations([]);
                }
            } finally {
                if (mounted) {
                    setOrganizationsLoading(false);
                }
            }
        };

        loadOrganizations();

        return () => {
            mounted = false;
        };
    }, [location?.id]);

    const searchTheme = useMemo(
        () => ({
            variables: {
                fontFamily: 'inherit',
                unit: '14px',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid var(--modal-input-border)',
                colorText: 'var(--rs-text)',
                colorBackground: 'var(--rs-option-bg)',
                colorBackgroundHover: 'var(--rs-option-hover-bg)',
                colorBackgroundActive: 'var(--rs-option-selected-bg)',
                colorPrimary: 'var(--btn-apply-bg)',
                colorSecondary: 'var(--modal-input-placeholder)',
                boxShadow: 'none',
            },
            cssText: `
                input {
                    min-height: 44px;
                    padding: 10px 12px;
                    border: 1px solid var(--modal-input-border);
                    border-radius: 8px;
                    background: var(--modal-input-bg);
                    color: var(--modal-input-text);
                    font-size: calc(var(--body-font-size) * var(--font-size-multiplier));
                }
                input::placeholder {
                    color: var(--modal-input-placeholder);
                }
                input:focus {
                    border-color: var(--modal-input-focus-border);
                    box-shadow: 0 0 0 1px var(--modal-input-focus-ring);
                    outline: none;
                }
                .Input {
                    color: var(--rs-text) !important;
                }
                .Input::placeholder {
                    color: var(--rs-placeholder) !important;
                }
                .Input:focus {
                    color: var(--rs-text) !important;
                }
                .Results {
                    background: var(--rs-menu-bg);
                    border: 1px solid var(--rs-menu-border);
                    border-radius: 8px;
                }
                .Suggestion {
                    background: var(--rs-option-bg);
                    color: var(--rs-text) !important;
                }
                .SuggestionName,
                .Suggestion * {
                    color: var(--rs-text) !important;
                }
                .Suggestion:hover {
                    background: var(--rs-option-hover-bg);
                    color: var(--rs-text) !important;
                }
                .Suggestion[aria-selected="true"] {
                    background: var(--rs-option-selected-bg);
                    color: var(--rs-option-selected-text) !important;
                }
                .Suggestion[aria-selected="true"] .SuggestionName,
                .Suggestion[aria-selected="true"] * {
                    color: var(--rs-option-selected-text) !important;
                }
                .Suggestion:active {
                    background: var(--rs-option-selected-bg);
                    color: var(--rs-option-selected-text) !important;
                }
                .ResultsAttribution,
                .ResultsAttribution a {
                    color: var(--rs-placeholder);
                }
            `,
        }),
        []
    );

    const [alertState, setAlertState] = useState({
        open: false,
        message: '',
        type: 'info',
        position: isRtl ? 'left' : 'right'
    });

    const showAlert = (type, message) => {
        setAlertState({
            open: true,
            type,
            message,
            position: isRtl ? 'left' : 'right'
        });
    };

    const currentLocationId = Number(location?.id);

    const assignedLocationByDeviceId = useMemo(() => {
        const map = new Map();
        (Array.isArray(devices) ? devices : []).forEach((device) => {
            const deviceId = Number(device?._row_id ?? device?.id);
            const locationId = Number(getDeviceLocationId(device));
            if (Number.isFinite(deviceId) && deviceId > 0) {
                map.set(deviceId, Number.isFinite(locationId) && locationId > 0 ? locationId : null);
            }
        });
        return map;
    }, [devices]);

    const selectedDeviceIdSet = useMemo(() => {
        return new Set(
            (formData.deviceIds || [])
                .map((id) => Number(id))
                .filter((id) => Number.isFinite(id) && id > 0)
        );
    }, [formData.deviceIds]);

    const deviceOptions = useMemo(() => {
        return (Array.isArray(devices) ? devices : [])
            .filter((device) => {
                const id = Number(device?._row_id ?? device?.id);
                const assignedLocationId = assignedLocationByDeviceId.get(id) || null;
                if (!Number.isFinite(id) || id <= 0) return false;
                return assignedLocationId === null || assignedLocationId === currentLocationId || selectedDeviceIdSet.has(id);
            })
            .map((device) => buildDeviceDropdownOption(device));
    }, [devices, assignedLocationByDeviceId, currentLocationId, selectedDeviceIdSet]);

    const selectedDeviceOptions = deviceOptions.filter((option) =>
        (formData.deviceIds || []).some((id) => Number(id) === Number(option.value))
    );

    const organizationOptions = useMemo(() => {
        return (Array.isArray(organizations) ? organizations : [])
            .filter((organization) => {
                const locationId = Number(organization?.location_id);
                return !Number.isFinite(locationId) || locationId <= 0 || locationId === Number(location?.id);
            })
            .map((organization) => ({
                value: Number(organization.id),
                label: organization.name || `Organization #${organization.id}`
            }))
            .filter((option) => Number.isInteger(option.value) && option.value > 0);
    }, [organizations, location?.id]);

    const selectedOrganizationOptions = organizationOptions.filter((option) =>
        (formData.organizationIds || []).some((id) => Number(id) === Number(option.value))
    );

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            backgroundColor: 'var(--rs-control-bg)',
            borderColor: state.isFocused
                ? 'var(--rs-control-border-focus)'
                : 'var(--rs-control-border)',
            boxShadow: state.isFocused ? 'var(--rs-control-focus-shadow)' : 'none',
            '&:hover': {
                borderColor: 'var(--rs-control-border-hover)',
            },
            color: 'var(--rs-text)',
            fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
            minHeight: '44px',
            borderRadius: 8,
            cursor: 'pointer',
        }),
        singleValue: (provided) => ({
            ...provided,
            color: 'var(--rs-text)',
            fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
        }),
        input: (provided) => ({
            ...provided,
            color: 'var(--rs-text)',
            fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
        }),
        placeholder: (provided) => ({
            ...provided,
            color: 'var(--rs-placeholder)',
            fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: 'var(--rs-menu-bg)',
            border: '1px solid var(--rs-menu-border)',
            borderRadius: 8,
            overflow: 'hidden',
            zIndex: 9999,
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? 'var(--rs-option-selected-bg)'
                : state.isFocused
                    ? 'var(--rs-option-hover-bg)'
                    : 'var(--rs-option-bg)',
            color: state.isSelected ? 'var(--rs-option-selected-text)' : 'var(--rs-text)',
            fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
            '&:active': {
                backgroundColor: 'var(--rs-option-active-bg)',
            },
            cursor: 'pointer',
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: 'var(--rs-option-hover-bg)',
            border: '1px solid var(--rs-menu-border)',
            borderRadius: 8,
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: 'var(--rs-text)',
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: 'var(--rs-text)',
            ':hover': {
                backgroundColor: 'transparent',
                color: 'var(--rs-indicator-hover)',
            },
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            color: 'var(--rs-indicator)',
            paddingRight: 12,
            '&:hover': {
                color: 'var(--rs-indicator-hover)',
            },
        }),
        clearIndicator: (provided) => ({
            ...provided,
            color: 'var(--rs-indicator)',
            '&:hover': {
                color: 'var(--rs-indicator-hover)',
            },
        }),
        indicatorSeparator: () => ({
            display: 'none',
        }),
    };

    const datasource = apiUrl(`location`);

    const geocodeAddress = async ({ silent = false } = {}) => {
        if (isGeocoding) return;

        const addressQuery = String(formData.address || '').trim();
        if (!addressQuery) {
            if (!silent) {
                showAlert(
                    'error',
                    t.validation?.addressRequired || 'Address is required to lookup coordinates.'
                );
            }
            return;
        }

        if (
            addressQuery === lastGeocodedAddress &&
            String(formData.latitude || '').trim() &&
            String(formData.longitude || '').trim()
        ) {
            return;
        }

        const token = process.env.REACT_APP_MAPBOX_TOKEN;
        if (!token) {
            if (!silent) {
                showAlert(
                    'error',
                    t.popup?.geoMissingToken ||
                        'Mapbox token is missing. Please set REACT_APP_MAPBOX_TOKEN.'
                );
            }
            return;
        }

        setIsGeocoding(true);
        try {
            const url =
                `https://api.mapbox.com/search/geocode/v6/forward` +
                `?q=${encodeURIComponent(addressQuery)}` +
                `&limit=1&access_token=${token}`;
            const res = await fetch(url);
            if (!res.ok) {
                throw new Error(t.popup?.geoRequestFailed || 'Failed to fetch coordinates.');
            }

            const data = await res.json();
            const feature = data?.features?.[0];

            let lng;
            let lat;
            if (Array.isArray(feature?.geometry?.coordinates)) {
                [lng, lat] = feature.geometry.coordinates;
            } else if (feature?.properties?.coordinates) {
                lng = feature.properties.coordinates.longitude;
                lat = feature.properties.coordinates.latitude;
            }

            const lngNum = Number(lng);
            const latNum = Number(lat);
            if (!Number.isFinite(lngNum) || !Number.isFinite(latNum)) {
                throw new Error(t.popup?.geoNoResults || 'No coordinates found for this address.');
            }

            setFormData((prev) => ({
                ...prev,
                latitude: String(latNum),
                longitude: String(lngNum),
            }));
            setLastGeocodedAddress(addressQuery);

            if (!silent) {
                showAlert(
                    'success',
                    t.popup?.geoSuccess || 'Coordinates updated from address.'
                );
            }
        } catch (err) {
            if (!silent) {
                showAlert(
                    'error',
                    err?.message || t.popup?.geoRequestFailed || 'Failed to fetch coordinates.'
                );
            }
        } finally {
            setIsGeocoding(false);
        }
    };

    const handleAddressRetrieve = (res) => {
        const feature = res?.features?.[0];
        if (!feature) return;

        let lng;
        let lat;
        if (Array.isArray(feature?.geometry?.coordinates)) {
            [lng, lat] = feature.geometry.coordinates;
        } else if (feature?.properties?.coordinates) {
            lng = feature.properties.coordinates.longitude;
            lat = feature.properties.coordinates.latitude;
        }

        const lngNum = Number(lng);
        const latNum = Number(lat);
        if (!Number.isFinite(lngNum) || !Number.isFinite(latNum)) {
            return;
        }

        const resolvedAddress =
            feature?.properties?.full_address ||
            feature?.properties?.name ||
            formData.address;

        setFormData((prev) => ({
            ...prev,
            address: resolvedAddress,
            latitude: String(latNum),
            longitude: String(lngNum),
        }));
        setLastGeocodedAddress(String(resolvedAddress || '').trim());
    };

    const updateLocation = async () => {
        if (submitting) return;

        if (!canEditLocations) {
            showAlert('error', t.noPermission);
            return;
        }

        const body = {
            id: location.id,
            device_ids: formData.deviceIds,
            organization_ids: formData.organizationIds,
            name: formData.name,
            address: formData.address,
            longitude: formData.longitude,
            latitude: formData.latitude
        };

        setSubmitting(true);
        try {
            const res = await axios.put(datasource, body, {
                headers: { 'Content-Type': 'application/json' }
            });
            showAlert(
                'success',
                res.data.message || 'Location updated successfully.'
            );

            refresh();

            setTimeout(() => {
                setState(false);
            }, 2000);
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            showAlert('error', errorMessage);
            setSubmitting(false);
        }
    };

    if (!canEditLocations) {
        return (
            <div
                id="modal"
                style={{
                    direction:
                        lang === 'ar' || lang === 'ur' ? 'rtl' : 'ltr'
                }}
            >
                <div
                    id="popup"
                    className="dashboard-modal"
                    style={{
                        animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
                        fontSize:
                            'calc(var(--body-font-size) * var(--font-size-multiplier))'
                    }}
                >
                    <div className="dashboard-header">
                        <div className="dashboard-header-icon">
                            <AddNewIcon />
                        </div>
                        <div className="dashboard-header-text">
                            <h2>{t.popup?.titleEdit || 'Permission Denied'}</h2>
                            <p>
                                {t.popup?.permissionDeniedMessageEdit ||
                                    t.noPermission}
                            </p>
                        </div>
                    </div>

                    <div className="modal-divider" />

                    <div className="modal-content dashboard-content" />

                    <div className="modal-footer dashboard-footer">
                        <button
                            className="btn btn-cancel"
                            onClick={() => setState(false)}
                        >
                            {t.popup?.buttonClose || 'Close'}
                        </button>
                        <div />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            id="modal"
            style={{
                direction: lang === 'ar' || lang === 'ur' ? 'rtl' : 'ltr'
            }}
        >
            <Alert
                open={alertState.open}
                close={() =>
                    setAlertState({ ...alertState, open: false })
                }
                message={alertState.message}
                type={alertState.type}
                position={alertState.position}
            />

            <div
                id="popup"
                className="dashboard-modal"
                style={{
                    animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
                    fontSize:
                        'calc(var(--body-font-size) * var(--font-size-multiplier))'
                }}
            >
                {/* HEADER */}
                <div className="dashboard-header">
                    <div className="dashboard-header-icon">
                        <AddNewIcon />
                    </div>
                    <div className="dashboard-header-text">
                        <h2>{t.popup?.headerEdit || 'Edit location'}</h2>
                        <p>
                            {t.popup?.descriptionEdit ||
                                'Update Location details in the form below'}
                        </p>
                    </div>
                </div>

                <div className="modal-divider" />

                {/* BODY */}
                <div className="modal-content dashboard-content">
                    {/* Name */}
                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-icon">
                                <NameIcon />
                            </span>
                            <span className="form-section-title-text">
                                {t.popup?.labelName || 'Name'}
                            </span>
                        </div>

                        <div className="form-item">
                            <div className="field-with-counter">
                                <input
                                    type="text"
                                    className="add-column"
                                    placeholder={
                                        t.popup?.placeholderName || 'Name'
                                    }
                                    maxLength={50}
                                    value={formData.name}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            name: e.target.value
                                        })
                                    }
                                />
                                <span className="field-counter">
                                    {formData.name?.length || 0} / 50
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Address */}
                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-icon">
                                <AddressIcon />
                            </span>
                            <span className="form-section-title-text">
                                {t.popup?.labelAddress || 'Address'}
                            </span>
                        </div>

                        <div className="form-item">
                            {mapboxToken ? (
                                <SearchBox
                                    accessToken={mapboxToken}
                                    value={formData.address}
                                    onChange={(value) =>
                                        setFormData({
                                            ...formData,
                                            address: value
                                        })
                                    }
                                    onRetrieve={handleAddressRetrieve}
                                    onBlur={() => {
                                        if (
                                            !String(formData.latitude || '').trim() &&
                                            !String(formData.longitude || '').trim()
                                        ) {
                                            geocodeAddress({ silent: true });
                                        }
                                    }}
                                    placeholder={
                                        t.popup?.placeholderAddress ||
                                        'Enter your address'
                                    }
                                    className="location-searchbox"
                                    theme={searchTheme}
                                    options={{ language: lang }}
                                />
                            ) : (
                                <input
                                    type="text"
                                    className="add-column"
                                    placeholder={
                                        t.popup?.placeholderAddress ||
                                        'Enter your address'
                                    }
                                    value={formData.address}
                                    onBlur={() => {
                                        if (
                                            !String(formData.latitude || '').trim() &&
                                            !String(formData.longitude || '').trim()
                                        ) {
                                            geocodeAddress({ silent: true });
                                        }
                                    }}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            address: e.target.value
                                        })
                                    }
                                />
                            )}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                            <button
                                type="button"
                                className="btn btn-apply btn-inline"
                                onClick={() => geocodeAddress()}
                                disabled={isGeocoding || !String(formData.address || '').trim()}
                            >
                                {isGeocoding
                                    ? (t.popup?.geoButtonLoading || 'Finding coordinates...')
                                    : (t.popup?.geoButton || 'Auto-fill coordinates')}
                            </button>
                        </div>
                    </div>

                    {/* Organizations */}
                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-icon">
                                <OrganizationIcon />
                            </span>
                            <span className="form-section-title-text">
                                {t.popup?.labelOrganization || 'Organizations'}
                            </span>
                        </div>

                        <div className="form-item">
                            <Select
                                id="location-organizations"
                                options={organizationOptions}
                                value={selectedOrganizationOptions}
                                onChange={(selectedOptions) =>
                                    setFormData({
                                        ...formData,
                                        organizationIds: Array.isArray(selectedOptions)
                                            ? selectedOptions.map((opt) => opt.value)
                                            : []
                                    })
                                }
                                isMulti
                                closeMenuOnSelect={false}
                                styles={customSelectStyles}
                                placeholder={
                                    organizationsLoading
                                        ? (t.popup?.placeholderOrganizationLoading || 'Loading organizations...')
                                        : (t.popup?.placeholderOrganization || 'Select organizations')
                                }
                                isClearable
                                isLoading={organizationsLoading}
                                isDisabled={organizationsLoading || organizationOptions.length === 0}
                                noOptionsMessage={() =>
                                    t.popup?.noOrganizations || 'No organizations available'
                                }
                            />
                        </div>
                    </div>

                    {/* Device */}
                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-icon">
                                <DeviceIdIcon />
                            </span>
                            <span className="form-section-title-text">
                                {t.popup?.labelDevice || 'Device'}
                            </span>
                        </div>

                        <div className="form-item">
                            <Select
                                id="location-device"
                                options={deviceOptions}
                                value={selectedDeviceOptions}
                                onChange={(selectedOptions) =>
                                    setFormData({
                                        ...formData,
                                        deviceIds: Array.isArray(selectedOptions)
                                            ? selectedOptions.map((opt) => opt.value)
                                            : []
                                    })
                                }
                                isMulti
                                closeMenuOnSelect={false}
                                styles={customSelectStyles}
                                placeholder={
                                    devicesLoading
                                        ? (t.popup?.placeholderDeviceLoading || 'Loading devices...')
                                        : (t.popup?.placeholderDevice || 'Select devices')
                                }
                                isClearable
                                isLoading={devicesLoading}
                                isDisabled={devicesLoading || deviceOptions.length === 0}
                                noOptionsMessage={() =>
                                    t.popup?.noDevices || 'No devices available'
                                }
                            />
                        </div>
                    </div>

                    {/* Latitude */}
                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-title-text">
                                {t.popup?.labelLatitude || 'Latitude'}
                            </span>
                        </div>

                        <div className="form-item">
                            <input
                                type="text"
                                inputMode="decimal"
                                className="add-column"
                                placeholder={t.popup?.placeholderLatitude}
                                value={formData.latitude}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        latitude: e.target.value
                                    })
                                }
                            />
                        </div>
                    </div>

                    {/* Longitude */}
                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-title-text">
                                {t.popup?.labelLongitude || 'Longitude'}
                            </span>
                        </div>

                        <div className="form-item">
                            <input
                                type="text"
                                inputMode="decimal"
                                className="add-column"
                                placeholder={t.popup?.placeholderLongitude}
                                value={formData.longitude}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        longitude: e.target.value
                                    })
                                }
                            />
                        </div>
                    </div>
                </div>

                {/* FOOTER */}
                <div className="modal-footer dashboard-footer">
                    <button
                        className="btn btn-cancel"
                        onClick={() => setState(false)}
                        disabled={submitting}
                    >
                        {t.popup?.buttonCancelEdit || 'Cancel'}
                    </button>
                    <button
                        className="btn btn-apply dashboard-primary-btn"
                        onClick={updateLocation}
                        disabled={submitting}
                    >
                        {t.popup?.buttonSaveEdit || 'Save Location'}
                    </button>
                </div>
            </div>
        </div>
    );
};
